import { createAuthGuard } from '@smartsash/auth';
import {
  createRouteGuard,
  toPermissionKeys,
  type RouteId,
  type Subject,
} from '@smartsash/permissions';
import { redirect } from '@tanstack/react-router';

import type { RouterContext } from '@/routeContext';
import { rememberWorkspaceOverride, stateWorkspace } from '@/services/auth/workspace';

/**
 * Route guards on the shared `@smartsash/auth` session (HS-859, C1). They replace the interim
 * HS-837 guards, which read a boolean snapshot pushed into the router by an in-React bridge.
 *
 * `createAuthGuard` awaits `auth.restore()` before deciding, so a valid cookie session no
 * longer flashes `/login` on a hard reload, and it intercepts `mfaRequired` /
 * `passwordChangeRequired` to their configured routes.
 *
 * Per-route permission enforcement (`requireRoute` below) rides on top: the root guard settles
 * the session first, so every permission decision reads a restored subject rather than an empty
 * one on a hard reload.
 */

/**
 * Routes reachable while signed out.
 *
 * Admin — unlike portal, whose root guard needs no exceptions — serves two PUBLIC onboarding
 * flows: a lab device sends a user to `/rfid-assignment` or `/device-registration` with a
 * session id, and that user has no admin session yet. `createAuthGuard` exempts only
 * `routes.login` and `routes.oauthCallback`, so without this list the root guard would bounce
 * both flows to `/login` and break card assignment and device registration in the field.
 */
const PUBLIC_PATHS = [
  '/login',
  '/auth/callback',
  '/rfid-assignment',
  '/device-registration',
] as const;

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

const guardAuthenticated = createAuthGuard<RouterContext>({
  getAuth: (context) => context.auth,
  redirect: (options) => redirect(options),
});

export interface GuardArgs {
  context: RouterContext;
  location: { href: string; pathname: string };
}

/**
 * Root `beforeLoad` (03-05 §5). Restore always runs — including on the public routes — so every
 * route reads a settled session and `requireAnon` below needs no restore of its own.
 *
 * On protected routes the restore is `createAuthGuard`'s own; the public branch has to do it
 * itself precisely because it skips that guard.
 */
export async function requireAuthentication(args: GuardArgs): Promise<void> {
  if (isPublicPath(args.location.pathname)) {
    await args.context.auth.restore();
    return;
  }
  await guardAuthenticated(args);
}

/** Public-only routes: bounce an already-authenticated user to the dashboard. */
export function requireAnon(context: RouterContext): void {
  if (context.auth.state.status === 'authenticated') {
    throw redirect({ to: '/dashboard' });
  }
}

/**
 * The session's authorization subject, read from the auth client rather than from React.
 *
 * `beforeLoad` runs outside the component tree, so `usePermission()` is unavailable here; the
 * client is the same one `SessionPermissionsProvider` feeds, so both sides see one permission
 * list. Effective permissions are the whole subject — the backend exposes no user-level role (a
 * role lives on an assignment since ADR-0001) — and `FullAccess` among them is the wildcard.
 *
 * Any non-authenticated state yields an empty subject. That is deliberate rather than fail-open:
 * the root `requireAuthentication` has already run and redirected, so the only way to reach a
 * permission guard is with a settled, authenticated session.
 */
function sessionSubject(context: RouterContext): Subject {
  const { state } = context.auth;
  const permissions = state.status === 'authenticated' ? (state.session.permissions ?? []) : [];
  return { permissions: toPermissionKeys(permissions) };
}

/**
 * Per-route permission enforcement, keyed on `ROUTE_PERMISSIONS` (HS-803 / C4).
 *
 * This was deferred for a long time because it is the one change that can strand a real user, so
 * three things bound the risk:
 *
 * - The decision comes from the route id, so this guard and the sidebar's `canRoute` filtering
 *   read the same entry and cannot disagree about who may open a screen.
 * - `FullAccess` satisfies every entry, so a Hawkins admin can always reach everything.
 * - `/dashboard` and `/settings` require nothing, so refusal always has somewhere to land.
 *
 * A refusal redirects to `/forbidden` carrying the attempted path, rather than throwing
 * `notFound()`: the route exists and the destination is real, the session simply may not open it,
 * and a 404 would send the user hunting for a broken link. Refusing in `beforeLoad` also stops
 * the route's loader, so a screen the subject cannot open never fires the request that would 403.
 *
 * These checks remain show/hide only — the server is authoritative.
 */
export const requireRoute: (routeId: RouteId) => (args: GuardArgs) => void =
  createRouteGuard<GuardArgs>({
    getSubject: (args) => sessionSubject(args.context),
    // The return annotation is load-bearing, not decoration: every route file imports this guard,
    // and TSR's `redirect` is typed against the generated route tree, which is built FROM those
    // files. Left to inference the two chase each other and `tsc` reports `requireRoute` as
    // implicitly `any`. Both annotations cut the cycle.
    redirect: (redirectTo, args): unknown =>
      redirect({
        to: redirectTo ?? '/forbidden',
        // The screen names where the subject was heading. Without it a bookmarked deep link just
        // becomes "access denied" with no clue which link is the dead one.
        search: { from: args.location.pathname },
      }),
  });

/** Signed-in routes both shells keep (HS-1630). `/change-password` sits outside `_dashboard`. */
const SHARED_WORKSPACE_PATHS = ['/notifications', '/settings', '/forbidden'] as const;

const INSTALLER_WORKSPACE_ROOT = '/installer';

function isUnder(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`);
}

/**
 * The shell boundary (HS-1630), on the `_dashboard` layout so it is decided in one place: an
 * installer stays inside `/installer/*` and the shared routes — `/dashboard`, post-login and every
 * admin route forward to `/installer` — and everyone else is kept out of `/installer/*`.
 *
 * It chooses a shell, it does not authorize: the routes inside still gate on permissions
 * (`requireRoute`), and the roles that pick the shell never grant anything
 * (`services/auth/workspace.ts`).
 */
export function requireWorkspace(args: GuardArgs): void {
  rememberWorkspaceOverride(args.location.href);
  const { pathname } = args.location;
  const inInstaller = isUnder(pathname, INSTALLER_WORKSPACE_ROOT);
  if (stateWorkspace(args.context.auth.state) === 'installer') {
    const shared = SHARED_WORKSPACE_PATHS.some((path) => isUnder(pathname, path));
    if (!inInstaller && !shared) throw redirect({ to: '/installer' });
    return;
  }
  if (inInstaller) throw redirect({ to: '/dashboard' });
}
